import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { AccessHistoryRow, AccessPerson, AccessSignInLinkEffect, AccessStaffRow, ApiFailure } from '../api/types'
import type { Api } from '../api'
import { Badge, Button, EmptyState, Loading, Notice, Segmented } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { HIERARCHY, Initials, Panel, StatusBadge, dayHeading, failureOf, messageOf, roleLabel, timeOf } from './accessWords'

/*
 * THE PERSON PAGE'S OTHER TABS (05 section 7.4): Hierarchy, History, and linking a
 * sign-in by hand. Main scope and Extra scopes, which change what a person sees,
 * are in AccessPersonEdit.tsx.
 */

/* =========================================================================
   HIERARCHY: their setting, who manages them and who they manage
   ========================================================================= */

const MODE_WORDS: Record<number, string> = {
  0: 'Every row in their companies, Business Units and Business Teams.',
  1: 'Only their own rows and their staff’s rows, and only inside their own companies, Business Units and Business Teams.',
  2: 'Their own and their staff’s rows, plus everything each staff member can see, with that staff member’s column groups.',
}

/**
 * Drawn as a chart (owner, 27 Oct 2026: "nothing understandable, arrows small, no
 * hovering"): the three settings with theirs marked, then their managers above them
 * and their staff below, every person a card that names them and opens their page.
 */
export function HierarchyTab({ api, person, onOpenPerson }: { api: Api; person: AccessPerson; onOpenPerson?: (id: number) => void }) {
  const mode = person.hierarchyMode
  // Their managers by name: the person record carries only their Mindzer IDs.
  const ids = useMemo(() => [...new Set([person.lineManagerId, person.funcManagerId].filter((x): x is number => x !== null))],
    [person.lineManagerId, person.funcManagerId])
  const [managers, setManagers] = useState<Record<number, AccessPerson | null | undefined>>({})
  useEffect(() => {
    let cancelled = false
    for (const id of ids) {
      api.accessPerson(id)
        .then((pg) => !cancelled && setManagers((m) => ({ ...m, [id]: pg.person })))
        .catch(() => !cancelled && setManagers((m) => ({ ...m, [id]: null })))
    }
    return () => {
      cancelled = true
    }
  }, [api, ids])

  const same = person.lineManagerId !== null && person.lineManagerId === person.funcManagerId
  const name = person.displayName ?? `Mindzer user ${person.mindzerUserId}`

  return (
    <>
      <Panel
        title="Hierarchy"
        aside={<span className="text-(length:--text-small) text-(--color-ink-3)">{person.hierarchyFrom === 'PERSON' ? 'Their own override' : person.hierarchyFrom === 'ROLE' ? 'From their HR Role' : 'No HR Role configured'}</span>}
      >
        <div className="grid grid-cols-1 gap-2 @3xl:grid-cols-3">
          {[0, 1, 2].map((m) => (
            <div
              key={m}
              aria-current={m === mode ? 'true' : undefined}
              className={`flex flex-col gap-1 rounded-md border px-3 py-2.5 ${
                m === mode ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)' : 'border-(--color-line) bg-(--color-surface)'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className={`font-semibold ${m === mode ? 'text-(--color-navy-700)' : 'text-(--color-ink-3)'}`}>{HIERARCHY[m]}</span>
                {m === mode && <Badge>Theirs</Badge>}
              </span>
              <span className={`text-(length:--text-small) ${m === mode ? 'text-(--color-ink-2)' : 'text-(--color-ink-4)'}`}>{MODE_WORDS[m]}</span>
            </div>
          ))}
        </div>
        {mode > 0 && (
          <p className="text-(length:--text-small) text-(--color-ink-3)">
            Rows with no salesperson are hidden. Material Planning and Inventory Aging have no salesperson, so hierarchy does not apply there.
          </p>
        )}
        {mode === 2 && person.staffAll > 0 && (
          <p className="rounded-md border border-(--color-warn)/30 bg-(--color-warn)/8 px-3 py-2 text-(length:--text-small) text-(--color-ink)">
            Type 2 gives this user everything their {person.staffAll} staff can see, with each staff member’s column groups, in any company.
            One wrong manager link in Mindzer hands a whole view to the wrong user.
          </p>
        )}
        {mode > 0 && person.staffAll === 0 && (
          <p className="text-(length:--text-small) text-(--color-ink-3)">Nobody reports to this user in Mindzer, so they see only rows where they are the salesperson.</p>
        )}
      </Panel>

      <Panel title="Reporting line" aside={<span className="text-(length:--text-small) text-(--color-ink-3)">Click a user to open their page</span>}>
        <div className="mx-auto flex w-full max-w-[720px] flex-col items-stretch">
          <div className={`grid grid-cols-1 gap-3 ${same ? '' : '@3xl:grid-cols-2'}`}>
            <ManagerCard role={same ? 'Line and functional manager' : 'Line manager'} id={person.lineManagerId}
              person={person.lineManagerId === null ? undefined : managers[person.lineManagerId]} onOpen={onOpenPerson} />
            {!same && (
              <ManagerCard role="Functional manager" id={person.funcManagerId}
                person={person.funcManagerId === null ? undefined : managers[person.funcManagerId]} onOpen={onOpenPerson} />
            )}
          </div>
          <Link />
          <div className="flex items-center gap-3 rounded-md border-2 border-(--color-navy-700) bg-(--color-navy-50) px-4 py-3">
            <Initials name={name} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="eyebrow">This user</span>
              <span className="truncate text-(length:--text-base) font-semibold text-(--color-ink)">{name}</span>
              <span className="truncate text-(length:--text-small) text-(--color-ink-3)">{person.roleName ? roleLabel(person) : 'No HR Role'}{person.compId ? ` · ${person.compId}` : ''}</span>
            </div>
            <div className="flex shrink-0 flex-col items-end text-(length:--text-small)">
              <span className="font-semibold text-(--color-ink) tabular-nums">{person.staffDirect} direct</span>
              <span className="text-(--color-ink-3) tabular-nums">{person.staffAll} at all levels</span>
            </div>
          </div>
          {person.staffDirect > 0 && <Link />}
        </div>
        {person.staffDirect === 0 ? (
          <p className="mx-auto mt-3 text-(length:--text-small) text-(--color-ink-3)">Nobody reports to them in Mindzer.</p>
        ) : (
          <div className="rounded-md border border-(--color-line) bg-(--color-surface-2) p-2">
            <div className="px-2 pb-2 pt-1">
              <span className="eyebrow">Their staff</span>
            </div>
            <StaffLevel api={api} managerId={person.mindzerUserId} depth={0} onOpen={onOpenPerson} />
          </div>
        )}
      </Panel>
    </>
  )
}

/** The line between two levels of the chart. */
function Link() {
  return <span aria-hidden className="mx-auto h-5 w-0.5 bg-(--color-line-strong)" />
}

/** One of their managers: a card that names them and opens their page. */
function ManagerCard({ role, id, person, onOpen }: {
  role: string
  id: number | null
  /** undefined while reading, null when it could not be read. */
  person: AccessPerson | null | undefined
  onOpen?: (id: number) => void
}) {
  if (id === null) {
    return (
      <div className="flex flex-col gap-0.5 rounded-md border border-dashed border-(--color-line-strong) px-4 py-3">
        <span className="eyebrow">{role}</span>
        <span className="text-(length:--text-small) text-(--color-ink-3)">None in Mindzer</span>
      </div>
    )
  }
  const name = person?.displayName ?? `Mindzer user ${id}`
  const body = (
    <>
      <Initials name={name} />
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="eyebrow">{role}</span>
        <span className="truncate font-semibold text-(--color-ink) group-hover:text-(--color-navy-700) group-hover:underline">
          {person === undefined ? 'Reading…' : name}
        </span>
        {person && <span className="truncate text-(length:--text-small) text-(--color-ink-3)">{person.roleName ? roleLabel(person) : 'No HR Role'}{person.compId ? ` · ${person.compId}` : ''}</span>}
      </span>
      {person && <StatusBadge status={person.status} />}
    </>
  )
  return onOpen ? (
    <button type="button" onClick={() => onOpen(id)} title={`Open ${name}’s page`}
      className="group flex items-center gap-3 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-3 transition-colors hover:border-(--color-navy-300) hover:bg-(--color-navy-50)">
      {body}
    </button>
  ) : (
    <div className="flex items-center gap-3 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-3">{body}</div>
  )
}

const EDGE: Record<AccessStaffRow['edge'], string> = { L: 'Line', F: 'Functional', LF: 'Line + functional' }

/** One manager's direct staff, each opening onto theirs. Loaded on demand. */
function StaffLevel({ api, managerId, depth, onOpen }: { api: Api; managerId: number; depth: number; onOpen?: (id: number) => void }) {
  const [rows, setRows] = useState<AccessStaffRow[] | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [open, setOpen] = useState<number[]>([])
  const [reload, setReload] = useState(0)
  // 200 at first; every one on request (7.4: "Show all 412").
  const [all, setAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api
      .accessStaff(managerId, all ? 100_000 : undefined)
      .then((s) => { if (!cancelled) { setRows(s.staff); setTotal(s.directTotal) } })
      .catch((e: unknown) => !cancelled && setFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, managerId, reload, all])

  if (failure) return <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
  if (!rows) return <Loading label="Reading their staff…" />
  if (rows.length === 0) return <p className="px-2 py-1 text-(length:--text-small) text-(--color-ink-3)">Nobody reports to them in Mindzer.</p>
  return (
    <ul className={`flex flex-col gap-0.5 ${depth > 0 ? 'ml-[22px] border-l-2 border-(--color-line-strong) pl-3' : ''}`}>
      {rows.map((s) => {
        const name = s.displayName ?? `Mindzer user ${s.staffUserId}`
        const isOpen = open.includes(s.staffUserId)
        return (
          <li key={s.staffUserId} className="flex flex-col">
            <div className="flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-(--color-surface)">
              {s.hasStaff ? (
                <button
                  type="button"
                  onClick={() => setOpen((o) => (isOpen ? o.filter((x) => x !== s.staffUserId) : [...o, s.staffUserId]))}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} who reports to ${name}`}
                  title={`${isOpen ? 'Hide' : 'Show'} who reports to ${name}`}
                  className={`grid h-7 w-7 shrink-0 place-items-center rounded-md border transition-colors ${
                    isOpen ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white'
                      : 'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) hover:border-(--color-navy-400) hover:bg-(--color-navy-100) hover:text-(--color-navy-700)'
                  }`}
                >
                  <Chevron open={isOpen} />
                </button>
              ) : (
                <span aria-hidden className="h-7 w-7 shrink-0" />
              )}
              <Initials name={name} size="sm" />
              {onOpen ? (
                <button type="button" onClick={() => onOpen(s.staffUserId)} title={`Open ${name}’s page`}
                  className="min-w-0 flex-1 truncate text-left font-medium text-(--color-ink) hover:text-(--color-navy-700) hover:underline">
                  {name}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate font-medium text-(--color-ink)">{name}</span>
              )}
              <span className="hidden w-32 shrink-0 text-(length:--text-small) text-(--color-ink-3) @3xl:block">{EDGE[s.edge]} report</span>
              <span className="w-14 shrink-0 text-(length:--text-small) text-(--color-ink-3)">{s.compId ?? '—'}</span>
              <span className="flex w-52 shrink-0 justify-end"><StatusBadge status={s.status} /></span>
            </div>
            {isOpen && <StaffLevel api={api} managerId={s.staffUserId} depth={depth + 1} onOpen={onOpen} />}
          </li>
        )
      })}
      {total !== null && total > rows.length && (
        <li className="flex items-center gap-2 px-2 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
          Showing {rows.length} of {total} direct staff.
          <button type="button" onClick={() => setAll(true)} className="font-medium text-(--color-navy-700) hover:underline">Show all {total}</button>
        </li>
      )}
    </ul>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={`h-3.5 w-3.5 transition-transform duration-150 motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}>
      <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* =========================================================================
   HISTORY: newest first, by day, filtered by kind, older on demand (01 rule 40)
   ========================================================================= */

const KINDS = [
  { value: '', label: 'All' },
  { value: 'APPROVAL', label: 'Approval' },
  { value: 'SETTINGS', label: 'Settings' },
  { value: 'ROLE', label: 'HR Role' },
  { value: 'MINDZER', label: 'Mindzer changes' },
  { value: 'MANAGERS', label: 'Managers' },
  { value: 'SIGN_IN', label: 'Sign-ins and Login As' },
] as const

/** Each kind's mark on the time line: the colour says what kind of change it was. */
const KIND_LOOK: Record<string, { label: string; dot: string; tag: string }> = {
  APPROVAL: { label: 'Approval', dot: 'bg-(--color-good)', tag: 'bg-(--color-good)/10 text-(--color-good)' },
  SETTINGS: { label: 'Settings', dot: 'bg-(--color-navy-700)', tag: 'bg-(--color-navy-100) text-(--color-navy-700)' },
  ROLE: { label: 'HR Role', dot: 'bg-(--color-privileged)', tag: 'bg-(--color-privileged)/10 text-(--color-privileged)' },
  MINDZER: { label: 'Mindzer', dot: 'bg-(--color-warn)', tag: 'bg-(--color-warn)/10 text-(--color-warn)' },
  MANAGERS: { label: 'Managers', dot: 'bg-(--color-ink-3)', tag: 'bg-(--color-surface-3) text-(--color-ink-2)' },
  SIGN_IN: { label: 'Sign-in', dot: 'bg-(--color-ink-4)', tag: 'bg-(--color-surface-3) text-(--color-ink-2)' },
}

/**
 * A person's history, or (role) an HR role's: every save of the role, with who and
 * why (05 7.6; review R-26). The role's rows have the same shape and no kinds.
 */
export function HistoryTab({ api, id, role = false }: { api: Api; id: number; role?: boolean }) {
  const [kind, setKind] = useState<(typeof KINDS)[number]['value']>('')
  const [rows, setRows] = useState<AccessHistoryRow[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [more, setMore] = useState(true)
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const read = useCallback(
    (asked: string, before?: string) => (role ? api.accessHrRoleHistory(id, before) : api.accessHistory(id, asked || undefined, before)),
    [api, id, role])

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setFailure(null)
    read(kind)
      .then((h) => { if (!cancelled) { setRows(h.history); setMore(h.history.length >= 100) } })
      .catch((e: unknown) => !cancelled && setFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [read, kind, reload])

  // The filter a page was asked for: a reply for another filter is dropped.
  const kindNow = useRef(kind)
  useEffect(() => {
    kindNow.current = kind
  }, [kind])

  const older = () => {
    const last = rows?.[rows.length - 1]
    if (!last || busy) return
    const asked = kind
    setBusy(true)
    read(asked, last.occurredUtc)
      .then((h) => {
        if (kindNow.current !== asked) return
        setRows((r) => [...(r ?? []), ...h.history])
        setMore(h.history.length >= 100)
      })
      .catch((e: unknown) => setFailure(failureOf(e)))
      .finally(() => setBusy(false))
  }

  // Newest first, one heading a day.
  const days = useMemo(() => {
    const out: { heading: string; rows: AccessHistoryRow[] }[] = []
    for (const r of rows ?? []) {
      const heading = dayHeading(r.occurredUtc)
      if (out.length && out[out.length - 1].heading === heading) out[out.length - 1].rows.push(r)
      else out.push({ heading, rows: [r] })
    }
    return out
  }, [rows])

  return (
    <Panel
      title="History"
      aside={<span className="text-(length:--text-small) text-(--color-ink-3)">{role
        ? 'Newest first: every save of this role, who made it and why.'
        : 'Newest first. Kept for everyone, leavers included.'}</span>}
    >
      {!role && <Segmented label="Which changes" value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k.value, label: k.label }))} />}
      {failure ? (
        <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
      ) : rows === null ? (
        <Loading label={role ? 'Reading the role’s history…' : 'Reading their history…'} />
      ) : rows.length === 0 ? (
        <EmptyState title={role ? 'This role has not been saved in MBI yet.' : 'Nothing recorded here yet.'} />
      ) : (
        <div className="flex flex-col gap-4">
          {days.map((d) => (
            <section key={d.heading} className="flex flex-col">
              <div className="flex items-center gap-3 pb-2">
                <h4 className="eyebrow shrink-0">{d.heading}</h4>
                <span aria-hidden className="h-px flex-1 bg-(--color-line)" />
              </div>
              <ol className="flex flex-col">
                {d.rows.map((r, i) => <HistoryEntry key={`${r.occurredUtc}-${i}`} row={r} last={i === d.rows.length - 1} />)}
              </ol>
            </section>
          ))}
          {more && (
            <div>
              <Button variant="default" onClick={older} disabled={busy}>{busy ? 'Reading…' : 'Show older'}</Button>
            </div>
          )}
        </div>
      )}
    </Panel>
  )
}

/** One change: when, what kind, what happened, who did it and why, and, on request, from what to what. */
function HistoryEntry({ row: r, last }: { row: AccessHistoryRow; last: boolean }) {
  const [open, setOpen] = useState(false)
  const look = KIND_LOOK[r.kind] ?? { label: r.kind, dot: 'bg-(--color-ink-4)', tag: 'bg-(--color-surface-3) text-(--color-ink-2)' }
  const changes = useMemo(() => diffOf(r.beforeJson, r.afterJson), [r.beforeJson, r.afterJson])
  return (
    <li className="grid grid-cols-[52px_20px_1fr] gap-x-2">
      <span className="pt-0.5 text-right text-(length:--text-small) text-(--color-ink-3) tabular-nums">{timeOf(r.occurredUtc)}</span>
      <span aria-hidden className="relative flex justify-center">
        <span className={`relative z-[1] mt-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-(--color-surface) ${look.dot}`} />
        {!last && <span className="absolute top-3 bottom-0 w-px bg-(--color-line-strong)" />}
      </span>
      <div className="flex min-w-0 flex-col gap-1.5 pb-4">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-(length:--text-micro) font-semibold ${look.tag}`}>{look.label}</span>
          <span className="text-(length:--text-small) font-medium text-(--color-ink)">{r.detail ?? r.what ?? r.kind}</span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-(length:--text-small) text-(--color-ink-3)">
          {/* A sign-in is the person's own act; anything else with no actor was MBI's. */}
          {r.actorEmail ? <span>by {r.actorEmail}</span> : r.kind !== 'SIGN_IN' && <span>by MBI</span>}
          {r.kind === 'ROLE' && <span className="text-(--color-ink-4)">affects everyone with this role</span>}
          {changes.length > 0 && (
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="font-medium text-(--color-navy-700) hover:underline">
              {open ? 'Hide what changed' : `What changed (${changes.length})`}
            </button>
          )}
        </div>
        {r.reason && (
          <blockquote className="rounded-md border-l-2 border-(--color-line-strong) bg-(--color-surface-2) px-3 py-1.5 text-(length:--text-small) text-(--color-ink-2)">
            “{r.reason}”
          </blockquote>
        )}
        {open && changes.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-(--color-line)">
            <table className="w-full border-collapse text-(length:--text-small)">
              <thead>
                <tr className="bg-(--color-surface-2) text-left text-(--color-ink-3)">
                  <th className="px-3 py-1.5 font-medium">What</th>
                  <th className="px-3 py-1.5 font-medium">Before</th>
                  <th className="px-3 py-1.5 font-medium">After</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.key} className="border-t border-(--color-line)">
                    <td className="px-3 py-1.5 font-medium text-(--color-ink)">{c.key.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-1.5 text-(--color-ink-3)">{c.before}</td>
                    <td className="px-3 py-1.5 text-(--color-ink)">{c.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </li>
  )
}

/**
 * The fields a recorded change moved, from its before and after (review R-27: the
 * server sent both and no screen showed them). Only the ones that differ; anything
 * that is not a JSON object is shown whole.
 */
function diffOf(before: string | null, after: string | null): { key: string; before: ReactNode; after: ReactNode }[] {
  if (!before && !after) return []
  const parse = (s: string | null): unknown => {
    if (!s) return null
    try {
      return JSON.parse(s)
    } catch {
      return s
    }
  }
  const a = parse(before)
  const b = parse(after)
  const show = (v: unknown, key?: string): ReactNode =>
    v === null || v === undefined ? <span className="text-(--color-ink-4)">—</span>
      : key === 'Hierarchy_Mode' && typeof v === 'number' ? (HIERARCHY[v] ?? String(v))
        : key && YES_NO_KEYS.has(key) && (v === 0 || v === 1) ? (v === 1 ? 'Yes' : 'No')
          : typeof v === 'boolean' ? (v ? 'Yes' : 'No')
            : Array.isArray(v) && v.every((x) => typeof x !== 'object') ? (v.length ? v.join(', ') : 'none')
              : typeof v === 'object' ? JSON.stringify(v)
                : typeof v === 'number' ? v.toLocaleString('en-GB')
                  : String(v)
  const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
  if ((a === null || isObj(a)) && (b === null || isObj(b))) {
    const keys = [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])]
    return keys
      .filter((k) => JSON.stringify((a ?? {})[k]) !== JSON.stringify((b ?? {})[k]))
      .map((k) => ({ key: KEY_WORDS[k] ?? k.replace(/_/g, ' '), before: show((a ?? {})[k], k), after: show((b ?? {})[k], k) }))
  }
  return [{ key: 'value', before: show(a), after: show(b) }]
}

/** The audit's own keys in screen words (deep review of 17g: the role's History tab showed them raw). */
const KEY_WORDS: Record<string, string> = {
  Set_Up: 'Configured in MBI', Hierarchy_Mode: 'Hierarchy', Mobile_Allowed: 'Phone allowed', Reports: 'Reports', Column_Groups: 'Column groups',
  // Before column groups (D53) a role held fixed figures; staging's history still says so.
  Figures: 'Figures (before column groups)',
  Holders: 'Users holding the role', People_Changed: 'Users changed', Entry_Gained: 'Users who gained entry',
  Entry_Lost: 'Users who lost entry', People_Widened: 'Users who see more', People_Narrowed: 'Users who see less',
  Companies_Gained: 'Companies gained', Column_Groups_Gained: 'Users gaining a column group', Cost_Figures_Gained: 'Cost figures gained (before column groups)', Managers_Affected: 'Managers affected',
  Widens: 'Widenings',
  // A scope line's own keys: always Business Unit and Business Team (owner, 30 Oct 2026); Business_Unit and Business_Team read right as they are.
  Comp_Id: 'Company', Any_Unit: 'Every Business Unit', Any_Team: 'Every Business Team',
}
const YES_NO_KEYS = new Set(['Set_Up', 'Mobile_Allowed', 'Any_Unit', 'Any_Team'])

/* =========================================================================
   LINK A SIGN-IN BY HAND (3.5, 7.7): when the automatic link cannot
   ========================================================================= */

export function SignInLink({ api, person, onChanged }: { api: Api; person: AccessPerson; onChanged: (text: string) => void }) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')
  // The preview, with the email and reason it was asked for: Link acts on exactly
  // those, and only while the boxes still say them (review, 26 Oct 2026).
  const [effect, setEffect] = useState<{ email: string; reason: string; answer: AccessSignInLinkEffect } | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)
  const unlink = person.signedIn
  const previewed = !!effect && effect.email === (unlink ? '' : email.trim()) && effect.reason === reason.trim()

  const review = () => {
    const asked = { email: unlink ? '' : email.trim(), reason: reason.trim() }
    if (busy || !asked.reason || (!unlink && !asked.email)) return
    setBusy(true)
    setNotice(null)
    api
      .accessSignInLink(person.mindzerUserId, { signInEmail: unlink ? undefined : asked.email, unlink, reason: asked.reason, preview: true })
      .then((r) => setEffect({ ...asked, answer: r }))
      .catch((e: unknown) => { setEffect(null); setNotice({ tone: 'bad', text: messageOf(e) }) })
      .finally(() => setBusy(false))
  }

  const act = () => {
    if (busy || !effect || !previewed) return
    setBusy(true)
    setNotice(null)
    api
      .accessSignInLink(person.mindzerUserId, { signInEmail: unlink ? undefined : effect.email, unlink, reason: effect.reason, preview: false })
      .then(() => {
        setOpen(false)
        setEffect(null)
        setReason('')
        setEmail('')
        onChanged(unlink ? 'Unlinked their sign-in. Their next sign-in links them again only if its email is theirs.' : 'Linked them to that sign-in.')
      })
      .catch((e: unknown) => { setEffect(null); setNotice({ tone: 'bad', text: messageOf(e) }) })
      .finally(() => setBusy(false))
  }

  // A placeholder account can never be linked, but a link it already has can be removed.
  if (person.status === 'PLACEHOLDER' && !unlink) return null
  const after = previewed ? effect!.answer.summary : null
  return (
    <div className="flex flex-col gap-2 text-(length:--text-small)">
      {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          className="self-start rounded px-1 font-medium text-(--color-ink-3) hover:text-(--color-navy-700) hover:underline">
          {unlink ? 'Unlink their sign-in…' : 'Link a sign-in by hand…'}
        </button>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2">
          {!unlink && (
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="The email they sign in to MBI with"
              className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1.5 text-(--color-ink)" />
          )}
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder={unlink ? 'Reason Of The Change (required), for example a recycled mailbox' : 'Reason Of The Change (required)'}
            className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1.5 text-(--color-ink)" />
          {after && (
            <p className="text-(--color-ink-2)">
              {unlink ? 'After unlinking' : 'After linking'} they {after.Can_Enter_After ? 'can enter MBI' : 'cannot enter MBI yet'}
              {after.Approved ? '.' : ': they are not approved.'}
            </p>
          )}
          <div className="flex gap-2">
            {!previewed ? (
              <Button variant="default" onClick={review} disabled={busy || !reason.trim() || (!unlink && !email.trim())}>Review</Button>
            ) : (
              <Button variant={unlink ? 'danger' : 'primary'} onClick={act} disabled={busy}>{unlink ? 'Unlink' : 'Link'}</Button>
            )}
            <Button variant="ghost" onClick={() => { setOpen(false); setEffect(null); setReason(''); setEmail('') }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  )
}
